<template>
  <div class="animated fadeIn">
    <template v-if="messageActivityLoaded">

      <!-- Top Row -->
      <div class="card-deck mb-3">

        <!-- Message Counts -->
        <b-card :no-body="true">
          <b-card-body class="p-3 clearfix">

            <div class="text-muted text-uppercase font-weight-bold font-s">Top {{ topChannelsChartData.length }} Channels</div>

            <top-channels-chart 
              v-if="messageActivityLoaded"
              :chart-data="topChannelsChartData"
              :channel-data="teamChannels"
              :label="'Top Channels By Message Count'"
              :data-key="'total_messages'"
              class="chart-wrapper" 
              style="min-height: 240px; height:88%; margin-top:10px;"/>

          </b-card-body>
        </b-card>


        <!-- Top Communicators - Messages -->
        <b-card :no-body="true">
          <b-card-body class="p-3 clearfix">

            <div class="text-muted text-uppercase font-weight-bold font-s">Top Communicators</div>

            <b-table class="mb-0 table-striped" responsive="sm" hover :items="topCommunicatorsByMessagesData" :fields="topCommunicatorsByMessagesFields" head-variant="light">

              <div slot="avatar" class="avatar" slot-scope="item">
                <img :src="item.value.url" class="img-avatar" alt="">
              </div>

              <div slot="teammate" slot-scope="item">
                <div>
                  <router-link :to="'/details/teammate?uid=' + item.item.slackId" class="unstyled-link">
                    {{item.value}}
                  </router-link>
                </div>
              </div>

              <div slot="messages" slot-scope="item">
                <div class="clearfix">
                  <div class="float-left">
                    <strong>{{item.value}}</strong>
                  </div>

                </div>
                <b-progress height={} class="progress-xs" v-model="item.value" :max="topCommunicatorByMessagesMax"></b-progress>
              </div>

            </b-table>

          </b-card-body>
        </b-card>


        <!-- Top Communicators - Word Count -->
        <b-card :no-body="true">
          <b-card-body class="p-3 clearfix">

            <div class="text-muted text-uppercase font-weight-bold font-s">Most Verbose Teammates</div>

            <b-table class="mb-0 table-striped" responsive="sm" hover :items="topCommunicatorsByWordsData" :fields="topCommunicatorsByWordsFields" head-variant="light">

              <div slot="avatar" class="avatar" slot-scope="item">
                <img :src="item.value.url" class="img-avatar" alt="">
              </div>

              <div slot="teammate" slot-scope="item">
                <div>
                  <router-link :to="'/details/teammate?uid=' + item.item.slackId" class="unstyled-link">
                    {{item.value}}
                  </router-link>
                </div>
              </div>

              <div slot="words" slot-scope="item">
                <div class="clearfix">
                  <div class="float-left">
                    <strong>&nbsp;</strong>
                  </div>

                </div>
                <b-progress height={} class="progress-xs" v-model="item.value" :max="topCommunicatorByWordsMax + (topCommunicatorByWordsMax * .15)"></b-progress>
              </div>

            </b-table>

          </b-card-body>
        </b-card>

      </div> 



      <!-- Main chart area -->
      <b-card>
        <b-row>
          <b-col sm="5">
            <template v-if="whichChart == 'team'">
              <h4 id="message-activity" class="card-title mb-0">My Team's Message Activity</h4>
            </template>
            <template v-else>
              <h4 id="message-activity" class="card-title mb-0">Selected Teammates Breakdown</h4>
            </template>
            <div class="small text-muted">{{ msgActivityStartDay }} - {{ currentDay }}</div>

            <p v-if="whichChart == 'team'" class="mt-3">
                <span class="bg-info text-white p-1 mr-2 rounded">
                  &nbsp;<i class="far fa-lightbulb mr-1"></i>
                  Tip! 
                </span>
                <span class="text-muted small">
                  Filter on teammates for a detailed message breakdown
                </span>
              </p>
          </b-col>
          <b-col sm="7" class="d-none d-md-block">
            

          </b-col>
        </b-row>

        <team-message-activity-chart
          v-if="messageActivityLoaded && whichChart == 'team'"
          :chart-data="messageActivityChartData"
          class="chart-wrapper" 
          style="height:300px;margin-top:20px;" 
          height="340" />

        <users-message-activity-chart
          v-if="messageActivityLoaded && whichChart == 'users'"
          :chart-data="messageActivityChartData"
          :user-data="teamMembers"
          class="chart-wrapper" 
          style="height:300px;margin-top:40px;" 
          height="340" />
        
      </b-card>


      <!-- Key Phrases -->
      <div class="card-deck mb-3">
        <b-card :no-body="true">
          <b-card-body class="p-3 clearfix">
            <div class="text-muted text-uppercase font-weight-bold font-s mb-2">
              Key Phrases - Selected Period
            </div>

            <grams-table :grams="topGrams"></grams-table>

          </b-card-body>
        </b-card>

        <b-card :no-body="true">
          <b-card-body class="p-3 clearfix">
            <div class="text-muted text-uppercase font-weight-bold font-s mb-2">
              Key Phrases - Trending
            </div>

            <grams-table :grams="trendingGrams"></grams-table>      

          </b-card-body>
        </b-card>
      </div>

    </template>
    <template v-else>
      <div class="sk-folding-cube align-middle">
        <div class="sk-cube1 sk-cube"></div>
        <div class="sk-cube2 sk-cube"></div>
        <div class="sk-cube4 sk-cube"></div>
        <div class="sk-cube3 sk-cube"></div>
      </div>
    </template>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'
import TeamMessageActivityChart from './messaging/TeamMessageActivityChart'
import UsersMessageActivityChart from './messaging/UsersMessageActivityChart'
import TopChannelsChart from './messaging/TopChannelsChart'
import GramsTable from './common/GramsTable.vue'
import moment from 'moment'
import { getStyle } from '@coreui/coreui-pro/dist/js/coreui-utilities'

export default {
  name: 'MessagingDashboard',
  components: {
    GramsTable,
    TeamMessageActivityChart,
    UsersMessageActivityChart,
    TopChannelsChart
  },
  data: function () {
    return {
      pageName: 'messaging',
      currentDay: moment().format('MMMM Do'),

      msgActivitySelected: 'Day',
      msgActivityStartDay: moment().subtract(1, 'd').format('MMMM Do'),

      successColor: getStyle('--success'),
      dangerColor: getStyle('--danger'),

      selectedTeammates: [],
      whichChart: null,

      topCommunicatorsByMessagesFields: {
        avatar: {
          label: '', //'<i class="icon-people"></i>',
          class: 'text-center'
        },
        teammate: {
          label: 'Teammate'
        },
        messages: {
          label: '# Messages'
        }
      },
      topCommunicatorsByWordsFields: {
        avatar: {
          label: '', //'<i class="icon-people"></i>',
          class: 'text-center'
        },
        teammate: {
          label: 'Teammate'
        },
        words: {
          label: 'Verbosity'
        }
      },
    }
    
  },

  watch: {
    timePeriod: function(period, lastPeriod) {
      if(this.$store.getters.currentPage === this.pageName) {
        this.updateMessageActivityData(period)
      }
    },
    filterUserIds: function(userIds, lastUserIds) {
      console.log('watched - filter UIDs: ' + userIds)

      this.updateMessageActivityData(this.timePeriod)
    }
  },

  computed: {
    timePeriod() {
      let p = this.$store.getters.timePeriod
      return p
    },
    filterUserIds() {
      return this.$store.getters.filterUserIds
    },
    messageActivityLoaded() {
      var loaded = this.$store.getters.birdseyeMessageActivityLoaded
      return loaded
    },
    topChannelsChartData() {
      // endpoint utilizes a method that fetches by period, returning a list of periods.
      // only one is fetched, use the first.  better than changing the format of the
      // api response where this type of data is returned IMO
      return this.$store.getters.birdseyeMessageActivityData.channels[0]
    },
    topCommunicatorsByMessagesData() {
      let formattedCommunicators = []

      if(this.whichChart == 'team') {
        let communicators = this.$store.getters.birdseyeMessageActivityData.communicators[0]
        
        for (const communicator of communicators) {
          let user = this.$store.getters.teamMember(communicator.user_id)
          // console.log(user)
          formattedCommunicators.push({
            avatar: {url: user.avatar},
            teammate: user.real_name,
            slackId: user.slack_id,
            messages: communicator.total_messages
          })
        }
      } else if(this.whichChart == 'users') {
        // iterate and sum over the chart data
        let counts = this.$store.getters.birdseyeMessageActivityData.message_counts
        var self = this
        Object.keys(counts).forEach(function(uid) {
          let d = counts[uid]
          let user = self.teamMembers.find(member => member.id == uid)

          const total_channel = Object.values(d).reduce((t, {channel}) => t + channel, 0)
          const total_direct = Object.values(d).reduce((t, {direct}) => t + direct, 0)
          const total_mpim = Object.values(d).reduce((t, {mpim}) => t + mpim, 0)

          formattedCommunicators.push({
            avatar: {url: user.avatar},
            teammate: user.real_name,
            slackId: user.slack_id,
            messages: total_channel + total_direct + total_mpim
          })
        })
        formattedCommunicators.sort((a, b) => (a.messages > b.messages) ? -1 : 1)
        formattedCommunicators = formattedCommunicators.slice(0, 5)  // top 5
      }

      return formattedCommunicators
    },
    topCommunicatorsByWordsData() {
      let formattedCommunicators = []

      if(this.whichChart == 'team') {
        let communicators = this.$store.getters.birdseyeMessageActivityData.communicators[0]
        
        for (const communicator of communicators) {
          let user = this.$store.getters.teamMember(communicator.user_id)
          // console.log(user)
          formattedCommunicators.push({
            avatar: {url: user.avatar},
            teammate: user.real_name,
            slackId: user.slack_id,
            words: communicator.total_words
          })
        }
      } else if(this.whichChart == 'users') {
        // iterate and sum over the chart data
        let counts = this.$store.getters.birdseyeMessageActivityData.message_counts
        var self = this
        Object.keys(counts).forEach(function(uid) {
          let d = counts[uid]
          let user = self.teamMembers.find(member => member.id == uid)

          const total_words = Object.values(d).reduce((t, {total_words}) => t + total_words, 0)

          formattedCommunicators.push({
            avatar: {url: user.avatar},
            teammate: user.real_name,
            slackId: user.slack_id,
            words: total_words
          })
        })
      }
      // always gotta sort this one because the response is sorted by messages
      formattedCommunicators.sort((a, b) => (a.words > b.words) ? -1 : 1)
      formattedCommunicators = formattedCommunicators.slice(0, 5)  // top 5
      return formattedCommunicators
    },
    messageActivityChartData() {
      return this.$store.getters.birdseyeMessageActivityData.message_counts
    },

    teamChannels() {
      return this.$store.getters.teamChannels
    },
    teamMembers() {
      return this.$store.getters.teamMembers
    },

    topGrams() {
      return this.$store.getters.birdseyeMessageActivityData.top_grams
    },
    trendingGrams() {
      return this.$store.getters.birdseyeMessageActivityData.trending_grams
    },

    topCommunicatorByMessagesMax() {
      if(this.whichChart == 'team') {
        let c = this.$store.getters.birdseyeMessageActivityData.communicators[0]
        if(c.length > 0) {
          return c[0].total_messages
        }
        return 100
      } else if(this.whichChart == 'users') {
        let c = this.topCommunicatorsByMessagesData
        if(c.length > 0) {
          return c[0].messages
        }
        return 100
      }
    },
    topCommunicatorByWordsMax() {
      let c = this.topCommunicatorsByWordsData
        if(c.length > 0) {
          return c[0].words
        }
        return 100
    }
  },
  
  methods: {
    teammatesSelected(value) {
      // don't need this method in it's current form but keeping in case we need to do anything else
      this.selectedTeammates = value
      // console.log(this.selectedTeammates)
    },
    
    updateMessageActivityData: function(period){

      if(this.filterUserIds.length == 0) {
        this.whichChart = 'team'
      } else {
        this.whichChart = 'users'
      }

      this.$store.dispatch('updateMessageActivityData', [period, this.filterUserIds])

      if(period == '6h') {
        this.msgActivityStartDay = moment().subtract(6, 'h').format('MMMM Do')
      } else if(period == '12h') {
        this.msgActivityStartDay = moment().subtract(12, 'h').format('MMMM Do')
      } else if(period == 'Day') {
        this.msgActivityStartDay = moment().subtract(1, 'd').format('MMMM Do')
      } else if(period == 'Week') {
        this.msgActivityStartDay = moment().subtract(7, 'd').format('MMMM Do')
      } else if(period == '2Weeks') {
        this.msgActivityStartDay = moment().subtract(14, 'd').format('MMMM Do')
      } else if(period == 'Month') {
        this.msgActivityStartDay = moment().subtract(30, 'd').format('MMMM Do')
      }
    }
  },

  mounted() {
    this.$store.commit('currentPage', this.pageName)

    // check on load.  user may have selected users, navigated from this page and returned, or changed filters on another page
    if(this.filterUserIds.length == 0) {
      this.whichChart = 'team'
    } else {
      this.whichChart = 'users'
    }

    this.updateMessageActivityData(this.timePeriod)
  }
}
</script>
